import { useState, useMemo, useCallback, useEffect } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { supabase } from '@/lib/supabase';
import { estadoNormalizado } from '@/utils/estadoUtils';

interface Avaliacao {
  id: string;
  formacao_id: string;
  funcionario_id: string;
  funcionario_nome: string | null;
  nota_geral: number;
  nota_conteudo: number | null;
  nota_formador: number | null;
  nota_organizacao: number | null;
  nota_aplicabilidade: number | null;
  comentario: string | null;
  recomendaria: boolean;
  created_at: string;
}

interface AvaliacaoForm {
  formacao_id: string;
  funcionario_id: string;
  nota_geral: number;
  nota_conteudo: number;
  nota_formador: number;
  nota_organizacao: number;
  nota_aplicabilidade: number;
  comentario: string;
  recomendaria: boolean;
}

function StarInput({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  const [hover, setHover] = useState(0);
  return (
    <div>
      <label className="block text-[12px] font-semibold text-zinc-600 mb-1">{label}</label>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="w-7 h-7 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
          >
            <i
              className={`${(hover || value) >= star ? 'ri-star-fill' : 'ri-star-line'} text-lg`}
              style={{ color: (hover || value) >= star ? '#f59e0b' : '#d4d4d8' }}
            />
          </button>
        ))}
        <span className="text-[12px] font-bold text-zinc-600 ml-1">{value}/5</span>
      </div>
    </div>
  );
}

function StarDisplay({ value, size = 'sm' }: { value: number; size?: 'sm' | 'md' }) {
  const color = value >= 4 ? '#10b981' : value >= 3 ? '#f59e0b' : '#ef4444';
  const iconSize = size === 'md' ? 'text-base' : 'text-xs';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
          <i className={`${value >= s ? 'ri-star-fill' : value >= s - 0.5 ? 'ri-star-half-fill' : 'ri-star-line'} ${iconSize}`} style={{ color: value >= s ? color : '#d4d4d8' }} />
        </div>
      ))}
      <span className="text-[11px] font-bold ml-1" style={{ color }}>{value.toFixed(1)}</span>
    </div>
  );
}

export default function AvaliacoesFormacaoPage() {
  const { formacoes, participantes } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [avaliacoes, setAvaliacoes] = useState<Avaliacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tab, setTab] = useState<'lista' | 'entidades' | 'formacoes'>('lista');
  const [search, setSearch] = useState('');
  const [formacaoFilter, setFormacaoFilter] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState<AvaliacaoForm>({
    formacao_id: '',
    funcionario_id: '',
    nota_geral: 4,
    nota_conteudo: 4,
    nota_formador: 4,
    nota_organizacao: 4,
    nota_aplicabilidade: 4,
    comentario: '',
    recomendaria: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadAvaliacoes = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('formacao_avaliacoes')
      .select('*')
      .order('created_at', { ascending: false });
    setAvaliacoes((data as Avaliacao[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAvaliacoes(); }, [loadAvaliacoes]);

  // ── Formações concluídas (elegíveis para avaliação) ────────────────────────
  const formacoesConcluidas = useMemo(() =>
    formacoes.filter((f) => estadoNormalizado(f.estado) === 'concluida'),
    [formacoes]
  );

  // ── Participantes da formação selecionada ─────────────────────────────────
  const participantesFormacao = useMemo(() => {
    if (!form.formacao_id) return [];
    return participantes
      .filter((p) => p.formacao_id === form.formacao_id)
      .map((p) => {
        const func = funcionarios.find((f) => f.id === p.funcionario_id);
        return { id: p.funcionario_id, nome: func?.nome ?? p.funcionario_nome ?? p.funcionario_id };
      });
  }, [form.formacao_id, participantes, funcionarios]);

  // ── Enriquecer avaliações ─────────────────────────────────────────────────
  const avaliacoesEnriquecidas = useMemo(() => {
    return avaliacoes.map((a) => {
      const formacao = formacoes.find((f) => f.id === a.formacao_id);
      return { ...a, nomeFormacao: formacao?.nome ?? '—', entidade: formacao?.entidade_formadora ?? '—' };
    });
  }, [avaliacoes, formacoes]);

  const filtered = useMemo(() => {
    return avaliacoesEnriquecidas.filter((a) => {
      const matchSearch = search === '' ||
        (a.funcionario_nome ?? '').toLowerCase().includes(search.toLowerCase()) ||
        a.nomeFormacao.toLowerCase().includes(search.toLowerCase());
      const matchFormacao = formacaoFilter === '' || a.formacao_id === formacaoFilter;
      return matchSearch && matchFormacao;
    });
  }, [avaliacoesEnriquecidas, search, formacaoFilter]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    if (avaliacoes.length === 0) return { mediaGeral: 0, recomendariam: 0, pctRecomenda: 0, total: 0 };
    const mediaGeral = avaliacoes.reduce((s, a) => s + a.nota_geral, 0) / avaliacoes.length;
    const recomendariam = avaliacoes.filter((a) => a.recomendaria).length;
    const pctRecomenda = Math.round((recomendariam / avaliacoes.length) * 100);
    return { mediaGeral, recomendariam, pctRecomenda, total: avaliacoes.length };
  }, [avaliacoes]);

  // ── Ranking por entidade ──────────────────────────────────────────────────
  const rankingEntidades = useMemo(() => {
    const mapa: Record<string, { entidade: string; notas: number[]; recomenda: number; total: number }> = {};
    avaliacoesEnriquecidas.forEach((a) => {
      if (!mapa[a.entidade]) mapa[a.entidade] = { entidade: a.entidade, notas: [], recomenda: 0, total: 0 };
      mapa[a.entidade].notas.push(a.nota_geral);
      if (a.recomendaria) mapa[a.entidade].recomenda++;
      mapa[a.entidade].total++;
    });
    return Object.values(mapa)
      .map((e) => ({
        ...e,
        media: e.notas.reduce((s, n) => s + n, 0) / e.notas.length,
        pctRecomenda: Math.round((e.recomenda / e.total) * 100),
      }))
      .sort((a, b) => b.media - a.media);
  }, [avaliacoesEnriquecidas]);

  // ── Ranking por formação ──────────────────────────────────────────────────
  const rankingFormacoes = useMemo(() => {
    const mapa: Record<string, { nome: string; entidade: string; notas: number[]; recomenda: number; total: number }> = {};
    avaliacoesEnriquecidas.forEach((a) => {
      if (!mapa[a.formacao_id]) mapa[a.formacao_id] = { nome: a.nomeFormacao, entidade: a.entidade, notas: [], recomenda: 0, total: 0 };
      mapa[a.formacao_id].notas.push(a.nota_geral);
      if (a.recomendaria) mapa[a.formacao_id].recomenda++;
      mapa[a.formacao_id].total++;
    });
    return Object.values(mapa)
      .map((f) => ({
        ...f,
        media: f.notas.reduce((s, n) => s + n, 0) / f.notas.length,
        pctRecomenda: Math.round((f.recomenda / f.total) * 100),
      }))
      .sort((a, b) => b.media - a.media);
  }, [avaliacoesEnriquecidas]);

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    if (!form.formacao_id) errs.formacao_id = 'Selecione uma formação';
    if (!form.funcionario_id) errs.funcionario_id = 'Selecione um participante';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    const func = funcionarios.find((f) => f.id === form.funcionario_id);
    const payload = {
      formacao_id: form.formacao_id,
      funcionario_id: form.funcionario_id,
      funcionario_nome: func?.nome ?? null,
      nota_geral: form.nota_geral,
      nota_conteudo: form.nota_conteudo,
      nota_formador: form.nota_formador,
      nota_organizacao: form.nota_organizacao,
      nota_aplicabilidade: form.nota_aplicabilidade,
      comentario: form.comentario || null,
      recomendaria: form.recomendaria,
    };
    await supabase.from('formacao_avaliacoes').insert(payload);
    await loadAvaliacoes();
    setShowModal(false);
    setSaving(false);
    setSuccessMsg('Avaliação registada com sucesso!');
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  async function handleDelete(id: string) {
    await supabase.from('formacao_avaliacoes').delete().eq('id', id);
    setAvaliacoes((prev) => prev.filter((a) => a.id !== id));
  }

  const TABS = [
    { key: 'lista' as const, label: 'Todas as Avaliações', icon: 'ri-list-check-2' },
    { key: 'formacoes' as const, label: 'Por Formação', icon: 'ri-book-open-line' },
    { key: 'entidades' as const, label: 'Por Entidade', icon: 'ri-building-2-line' },
  ];

  return (
    <>
      <main className="flex-1 bg-slate-50/60 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-star-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Avaliações de Formação</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Satisfação dos participantes · Ranking de entidades e formações</p>
              </div>
            </div>
            <button
              onClick={() => {
                setForm({ formacao_id: '', funcionario_id: '', nota_geral: 4, nota_conteudo: 4, nota_formador: 4, nota_organizacao: 4, nota_aplicabilidade: 4, comentario: '', recomendaria: true });
                setErrors({});
                setShowModal(true);
              }}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
              Registar Avaliação
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-zinc-100 px-8">
          <div className="flex items-center gap-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap flex items-center gap-2 px-4 py-3.5 text-[13px] font-semibold border-b-2 transition-colors cursor-pointer ${tab === t.key ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${t.icon} text-sm`} /></div>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">
          {successMsg && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-4 py-3 rounded-xl">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-sm" /></div>
              {successMsg}
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: 'ri-survey-line', value: kpis.total, label: 'Avaliações Totais', bg: 'bg-zinc-100 text-zinc-600' },
              { icon: 'ri-star-fill', value: kpis.mediaGeral > 0 ? kpis.mediaGeral.toFixed(1) : '—', label: 'Nota Média Geral', bg: kpis.mediaGeral >= 4 ? 'bg-emerald-50 text-emerald-600' : kpis.mediaGeral >= 3 ? 'bg-amber-50 text-amber-600' : 'bg-rose-50 text-rose-600' },
              { icon: 'ri-thumb-up-line', value: `${kpis.pctRecomenda}%`, label: 'Recomendariam', bg: kpis.pctRecomenda >= 80 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600' },
              { icon: 'ri-building-2-line', value: rankingEntidades.length, label: 'Entidades Avaliadas', bg: 'bg-zinc-100 text-zinc-600' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                  <i className={`${s.icon} text-base`} />
                </div>
                <div>
                  <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                  <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
              </div>
              <p className="text-[13px] text-zinc-400">A carregar avaliações...</p>
            </div>
          ) : (
            <>
              {/* ── TAB: LISTA ── */}
              {tab === 'lista' && (
                <>
                  <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
                    <div className="relative flex-1 min-w-[200px]">
                      <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                        <i className="ri-search-line text-sm" />
                      </div>
                      <input
                        type="text"
                        placeholder="Pesquisar por participante ou formação..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                      />
                    </div>
                    <select
                      value={formacaoFilter}
                      onChange={(e) => setFormacaoFilter(e.target.value)}
                      className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer max-w-[220px]"
                    >
                      <option value="">Todas as formações</option>
                      {formacoesConcluidas.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                    </select>
                    <span className="text-[12px] text-zinc-400 ml-auto">
                      <span className="font-semibold text-zinc-600">{filtered.length}</span> avaliação(ões)
                    </span>
                  </div>

                  {filtered.length === 0 ? (
                    <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center">
                        <i className="ri-star-line text-4xl text-zinc-200" />
                      </div>
                      <p className="text-[14px] font-semibold text-zinc-400">
                        {avaliacoes.length === 0 ? 'Ainda não há avaliações registadas' : 'Nenhuma avaliação encontrada'}
                      </p>
                      {avaliacoes.length === 0 && (
                        <button
                          onClick={() => setShowModal(true)}
                          className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 cursor-pointer"
                        >
                          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                          Registar primeira avaliação
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filtered.map((a) => (
                        <div key={a.id} className="bg-white rounded-xl border border-zinc-100 p-5">
                          <div className="flex items-start gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <p className="text-[13px] font-bold text-zinc-900">{a.funcionario_nome ?? a.funcionario_id}</p>
                                {a.recomendaria && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                                    Recomenda
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-zinc-500 mb-2">{a.nomeFormacao} · {a.entidade}</p>
                              <StarDisplay value={a.nota_geral} size="md" />
                              {a.comentario && (
                                <p className="text-[12px] text-zinc-600 mt-2 italic">&ldquo;{a.comentario}&rdquo;</p>
                              )}
                            </div>
                            <div className="flex-shrink-0 space-y-1 text-right">
                              {[
                                { label: 'Conteúdo', value: a.nota_conteudo },
                                { label: 'Formador', value: a.nota_formador },
                                { label: 'Organização', value: a.nota_organizacao },
                                { label: 'Aplicabilidade', value: a.nota_aplicabilidade },
                              ].filter((x) => x.value !== null).map((x) => (
                                <div key={x.label} className="flex items-center gap-2 justify-end">
                                  <span className="text-[10px] text-zinc-400">{x.label}</span>
                                  <div className="flex items-center gap-0.5">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                      <div key={s} className="w-2.5 h-2.5 flex items-center justify-center">
                                        <i className={`${(x.value ?? 0) >= s ? 'ri-star-fill' : 'ri-star-line'} text-[9px]`} style={{ color: (x.value ?? 0) >= s ? '#f59e0b' : '#d4d4d8' }} />
                                      </div>
                                    ))}
                                    <span className="text-[10px] font-bold text-zinc-600 ml-1">{x.value}</span>
                                  </div>
                                </div>
                              ))}
                              <div className="flex items-center gap-2 justify-end mt-2">
                                <span className="text-[10px] text-zinc-400">
                                  {new Date(a.created_at).toLocaleDateString('pt-PT')}
                                </span>
                                <button
                                  onClick={() => handleDelete(a.id)}
                                  className="whitespace-nowrap w-6 h-6 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line text-xs" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── TAB: POR FORMAÇÃO ── */}
              {tab === 'formacoes' && (
                <div className="space-y-3">
                  {rankingFormacoes.length === 0 ? (
                    <div className="bg-white rounded-xl border border-zinc-100 py-16 flex flex-col items-center gap-2">
                      <i className="ri-book-open-line text-3xl text-zinc-200" />
                      <p className="text-[13px] text-zinc-400">Sem avaliações por formação</p>
                    </div>
                  ) : rankingFormacoes.map((f, i) => (
                    <div key={f.nome} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4 flex-wrap">
                      <span className={`w-8 h-8 flex items-center justify-center rounded-full text-[12px] font-black flex-shrink-0 ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-zinc-300 text-zinc-700' : i === 2 ? 'bg-amber-700/70 text-white' : 'bg-zinc-100 text-zinc-500'}`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold text-zinc-900">{f.nome}</p>
                        <p className="text-[11px] text-zinc-400 mt-0.5">{f.entidade} · {f.total} avaliação(ões)</p>
                        <StarDisplay value={f.media} size="md" />
                      </div>
                      <div className="flex items-center gap-6 flex-shrink-0">
                        <div className="text-center">
                          <p className="text-[18px] font-black text-zinc-900">{f.media.toFixed(1)}</p>
                          <p className="text-[10px] text-zinc-400">média</p>
                        </div>
                        <div className="text-center">
                          <p className={`text-[18px] font-black ${f.pctRecomenda >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>{f.pctRecomenda}%</p>
                          <p className="text-[10px] text-zinc-400">recomenda</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── TAB: POR ENTIDADE ── */}
              {tab === 'entidades' && (
                <div className="space-y-3">
                  {rankingEntidades.length === 0 ? (
                    <div className="bg-white rounded-xl border border-zinc-100 py-16 flex flex-col items-center gap-2">
                      <i className="ri-building-2-line text-3xl text-zinc-200" />
                      <p className="text-[13px] text-zinc-400">Sem avaliações por entidade</p>
                    </div>
                  ) : rankingEntidades.map((e, i) => (
                    <div key={e.entidade} className={`bg-white rounded-xl border overflow-hidden ${i === 0 ? 'border-amber-200' : 'border-zinc-100'}`}>
                      {i === 0 && (
                        <div className="bg-amber-400 px-4 py-1.5 flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-trophy-line text-xs text-white" /></div>
                          <span className="text-[11px] font-bold text-white uppercase tracking-wide">Melhor Avaliada</span>
                        </div>
                      )}
                      <div className="p-5 flex items-center gap-4 flex-wrap">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${i === 0 ? 'bg-zinc-900' : 'bg-zinc-100'}`}>
                          <span className={`text-[11px] font-black ${i === 0 ? 'text-white' : 'text-zinc-600'}`}>
                            {e.entidade.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-bold text-zinc-900">{e.entidade}</p>
                          <p className="text-[11px] text-zinc-400 mt-0.5">{e.total} avaliação(ões)</p>
                          <StarDisplay value={e.media} size="md" />
                        </div>
                        <div className="flex items-center gap-6 flex-shrink-0">
                          <div className="text-center">
                            <p className="text-[22px] font-black text-zinc-900">{e.media.toFixed(1)}</p>
                            <p className="text-[10px] text-zinc-400">nota média</p>
                          </div>
                          <div className="text-center">
                            <p className={`text-[22px] font-black ${e.pctRecomenda >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>{e.pctRecomenda}%</p>
                            <p className="text-[10px] text-zinc-400">recomenda</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modal registar avaliação */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
                  <i className="ri-star-line text-white text-base" />
                </div>
                <div>
                  <h2 className="text-[16px] font-bold text-zinc-900">Registar Avaliação</h2>
                  <p className="text-[11px] text-zinc-400">Satisfação do participante com a formação</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-7 py-5 space-y-4">
              {/* Formação */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Formação <span className="text-rose-500">*</span></label>
                <select
                  value={form.formacao_id}
                  onChange={(e) => setForm((p) => ({ ...p, formacao_id: e.target.value, funcionario_id: '' }))}
                  className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.formacao_id ? 'border-rose-400' : 'border-zinc-200'}`}
                >
                  <option value="">Selecionar formação concluída...</option>
                  {formacoesConcluidas.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
                {errors.formacao_id && <p className="text-[11px] text-rose-500 mt-1">{errors.formacao_id}</p>}
              </div>

              {/* Participante */}
              {form.formacao_id && (
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Participante <span className="text-rose-500">*</span></label>
                  <select
                    value={form.funcionario_id}
                    onChange={(e) => setForm((p) => ({ ...p, funcionario_id: e.target.value }))}
                    className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.funcionario_id ? 'border-rose-400' : 'border-zinc-200'}`}
                  >
                    <option value="">Selecionar participante...</option>
                    {participantesFormacao.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                  </select>
                  {errors.funcionario_id && <p className="text-[11px] text-rose-500 mt-1">{errors.funcionario_id}</p>}
                </div>
              )}

              {/* Notas */}
              <div className="bg-zinc-50 rounded-xl p-4 space-y-3">
                <p className="text-[12px] font-bold text-zinc-700 mb-2">Avaliação por Dimensão</p>
                <StarInput value={form.nota_geral} onChange={(v) => setForm((p) => ({ ...p, nota_geral: v }))} label="Nota Geral *" />
                <div className="grid grid-cols-2 gap-3">
                  <StarInput value={form.nota_conteudo} onChange={(v) => setForm((p) => ({ ...p, nota_conteudo: v }))} label="Conteúdo" />
                  <StarInput value={form.nota_formador} onChange={(v) => setForm((p) => ({ ...p, nota_formador: v }))} label="Formador" />
                  <StarInput value={form.nota_organizacao} onChange={(v) => setForm((p) => ({ ...p, nota_organizacao: v }))} label="Organização" />
                  <StarInput value={form.nota_aplicabilidade} onChange={(v) => setForm((p) => ({ ...p, nota_aplicabilidade: v }))} label="Aplicabilidade" />
                </div>
              </div>

              {/* Comentário */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Comentário <span className="text-zinc-400 font-normal">(opcional)</span></label>
                <textarea
                  value={form.comentario}
                  onChange={(e) => setForm((p) => ({ ...p, comentario: e.target.value }))}
                  rows={3}
                  placeholder="Partilhe a sua opinião sobre a formação..."
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
                  maxLength={500}
                />
              </div>

              {/* Recomendaria */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, recomendaria: !p.recomendaria }))}
                  className={`whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-[13px] font-semibold transition-all cursor-pointer ${form.recomendaria ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-zinc-200 bg-white text-zinc-500'}`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${form.recomendaria ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-sm`} />
                  </div>
                  {form.recomendaria ? 'Recomendaria esta formação' : 'Não recomendaria'}
                </button>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
                <button onClick={() => setShowModal(false)} className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 cursor-pointer">
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 cursor-pointer disabled:opacity-50"
                >
                  {saving ? <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>A guardar...</> : 'Guardar Avaliação'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
